import React from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  ShowChart as ShowChartIcon,
  AccountBalance as AccountBalanceIcon,
  Business as BusinessIcon,
  Diversity3 as Diversity3Icon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { macroForecastAPI } from '../../services/api';

/**
 * MacroOutlookSummary Component
 * 
 * Provides a comprehensive "Big Picture" summary of the macro forecast
 * instead of listing individual indicators separately.
 * 
 * Analyzes all indicators and synthesizes them into:
 * - Overall economic outlook
 * - Key trends and drivers
 * - Risk assessment
 * - Investment implications
 */
const MacroOutlookSummary = ({ dashboardData, selectedHorizon = '3M' }) => {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;

  // Fetch outlook summary from backend API
  const {
    data: analysis,
    isLoading: loadingAnalysis,
    error: analysisError
  } = useQuery({
    queryKey: ['macroOutlookSummary', selectedHorizon],
    queryFn: async () => {
      const response = await macroForecastAPI.getOutlookSummary(selectedHorizon);
      return response.data;
    },
    enabled: !!dashboardData?.targets, // Only fetch if we have targets
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });

  if (!dashboardData || !dashboardData.targets) {
    return null;
  }

  // Check data freshness based on each indicator's update frequency
  // Macro indicators have different update frequencies (daily, weekly, monthly, quarterly, yearly)
  const getStaleThreshold = (frequency) => {
    const thresholds = {
      'daily': 3,        // Daily data: stale after 3 days
      'weekly': 10,       // Weekly data: stale after 10 days
      'monthly': 45,      // Monthly data: stale after 45 days (1.5 months)
      'quarterly': 100,   // Quarterly data: stale after 100 days (~3.3 months)
      'yearly': 200,      // Yearly data: stale after 200 days (~6.7 months)
    };
    return thresholds[frequency?.toLowerCase()] || 45; // Default to monthly threshold
  };

  // Check each target individually based on its frequency
  const staleTargets = dashboardData.targets
    .map(t => {
      if (!t.latest_actual?.date) return null;
      
      const latestDate = new Date(t.latest_actual.date);
      const daysSinceUpdate = Math.floor((new Date() - latestDate) / (1000 * 60 * 60 * 24));
      const threshold = getStaleThreshold(t.frequency);
      
      if (daysSinceUpdate > threshold) {
        return {
          name: t.target_name?.[currentLang] || t.target_name?.en || t.indicator_id,
          daysSinceUpdate,
          frequency: t.frequency || 'monthly',
          threshold
        };
      }
      return null;
    })
    .filter(t => t !== null);

  // Overall freshness check - use the most stale target
  const mostStaleTarget = staleTargets.length > 0
    ? staleTargets.reduce((max, t) => t.daysSinceUpdate > max.daysSinceUpdate ? t : max, staleTargets[0])
    : null;
  
  const isDataStale = mostStaleTarget !== null;

  // Show loading state
  if (loadingAnalysis) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, mb: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (analysisError || !analysis) {
    return (
      <Box sx={{ mb: 3, p: 2, bgcolor: 'error.lighter', borderRadius: 2 }}>
        <Typography variant="body2" color="error">
          {analysisError?.message || 'Failed to load macro outlook summary'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* Data Freshness Warning */}
      {isDataStale && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: '#fff3cd',
          borderRadius: 2,
          border: '2px solid',
          borderColor: '#ffc107',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          boxShadow: '0 2px 8px rgba(255, 193, 7, 0.2)'
        }}>
          <Box sx={{ 
            color: '#856404',
            fontSize: '1.5rem',
            lineHeight: 1,
            mt: 0.25
          }}>
            ⚠️
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#856404',
                fontWeight: 600,
                mb: 0.5,
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}
            >
              {staleTargets.length === 1 
                ? t('forecast.staleWarning', 'Data {{name}} has not been updated for {{days}} days', { name: mostStaleTarget.name, days: mostStaleTarget.daysSinceUpdate })
                : t('forecast.staleWarningMultiple', '{{count}} indicators are overdue for update', { count: staleTargets.length })
              }
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#856404',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                opacity: 0.9
              }}
            >
              {mostStaleTarget && (
                <>
                  {t('forecast.dataFrequency', 'Data is typically updated periodically.', { frequency: mostStaleTarget.frequency })}
                  {mostStaleTarget.daysSinceUpdate > mostStaleTarget.threshold * 1.5 && 
                    ` ${t('forecast.staleNote', 'Outlook score may not reflect current conditions.')}`
                  }
                </>
              )}
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Top: Score + Summary in one line */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 3,
        mb: 3,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        {/* Score - Bigger */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 140,
          position: 'relative'
        }}>
          <Typography variant="h1" sx={{ 
            fontWeight: 900,
            fontSize: '4rem',
            lineHeight: 1,
            background: analysis.sentiment === 'positive' 
              ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)'
              : analysis.sentiment === 'negative'
              ? 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)'
              : 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}>
            {analysis.score}
          </Typography>
          <Typography variant="caption" sx={{ 
            mt: 0.5,
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            color: 'text.secondary',
            mb: 1.5
          }}>
            {t('forecast.score', 'Score')}
          </Typography>
          <Box
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: 3,
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: '#ffffff',
              background: analysis.sentiment === 'positive'
                ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)'
                : analysis.sentiment === 'negative'
                ? 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)'
                : 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)',
              boxShadow: analysis.sentiment === 'positive'
                ? '0 4px 12px rgba(46, 125, 50, 0.3)'
                : analysis.sentiment === 'negative'
                ? '0 4px 12px rgba(198, 40, 40, 0.3)'
                : '0 4px 12px rgba(245, 124, 0, 0.3)',
              border: 'none',
              display: 'inline-block',
              whiteSpace: 'nowrap'
            }}
          >
            {analysis.headline[currentLang].replace(/[✅⚠️➡️]/g, '').trim()}
          </Box>
        </Box>

        {/* Summary Text - Enhanced */}
        <Box sx={{ 
          flex: 1,
          p: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2.5,
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              minHeight: 56,
            }}
          >
            <Box sx={{ 
              mr: 1.5, 
              color: 'primary.main', 
              display: 'flex', 
              alignItems: 'flex-start',
              mt: 0.25
            }}>
              <ShowChartIcon fontSize="medium" />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                fontWeight: 400,
                color: 'text.primary',
                lineHeight: 1.8,
                letterSpacing: 0.1,
                m: 0,
                p: 0,
              }}
            >
              {analysis.summary[currentLang]}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Bottom: Themes + Implications */}
      <Grid container spacing={2}>
        {/* Themes - No borders, just data */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="overline" sx={{ 
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: 1,
                    color: 'text.secondary'
                  }}>
                    {t('forecast.growth', 'GROWTH')}
                  </Typography>
                  {getStatusIcon(analysis.growth.status)}
                </Box>
                {analysis.growth.metrics.map((metric, idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {typeof metric.label === 'object' ? metric.label[currentLang] : metric.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {metric.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="overline" sx={{ 
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: 1,
                    color: 'text.secondary'
                  }}>
                    {t('forecast.inflation', 'INFLATION')}
                  </Typography>
                  {getStatusIcon(analysis.inflation.status)}
                </Box>
                {analysis.inflation.metrics.map((metric, idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {typeof metric.label === 'object' ? metric.label[currentLang] : metric.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {metric.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="overline" sx={{ 
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: 1,
                    color: 'text.secondary'
                  }}>
                    {t('forecast.risk', 'RISK')}
                  </Typography>
                  {getStatusIcon(analysis.risk.status)}
                </Box>
                {analysis.risk.metrics.map((metric, idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {typeof metric.label === 'object' ? metric.label[currentLang] : metric.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {metric.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Implications - Simple list */}
        <Grid item xs={12} md={4}>
          <Typography variant="overline" sx={{ 
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: 1,
            color: 'text.secondary',
            mb: 1.5,
            display: 'block'
          }}>
            {t('forecast.investmentImplications', 'INVESTMENT IMPLICATIONS')}
          </Typography>
          {analysis.implications.map((implication, index) => {
            // Map implication titles to icons
            const iconMap = {
              'Equities': <ShowChartIcon />,
              'Bonds': <AccountBalanceIcon />,
              'Real Estate': <BusinessIcon />,
              'Diversification': <Diversity3Icon />
            };
            const icon = iconMap[implication.title?.en || implication.title?.[currentLang]] || <ShowChartIcon />;
            
            return (
            <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ 
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 32,
                minHeight: 32,
                fontSize: '1.6rem',
                mr: 1
              }}>
                {React.cloneElement(icon, { fontSize: 'inherit' })}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.92rem', mb: 0.3, color: 'text.primary' }}>
                  {implication.title[currentLang]}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.87rem', lineHeight: 1.5, color: 'text.primary', opacity: 0.85 }}>
                  {implication.description[currentLang]}
                </Typography>
              </Box>
            </Box>
            );
          })}
        </Grid>
      </Grid>
    </Box>
  );
};

// Status Icon Helper (moved outside component)
const getStatusIcon = (status) => {
  switch (status) {
    case 'positive': return <TrendingUpIcon sx={{ fontSize: 18, color: 'success.main' }} />;
    case 'negative': return <TrendingDownIcon sx={{ fontSize: 18, color: 'error.main' }} />;
    default: return <TrendingFlatIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
  }
};

// REMOVED: analyzeMacroOutlook function - all analysis is now done server-side
// via /macro-forecast/outlook-summary endpoint
// This ensures consistent calculations and proper separation of concerns

export default MacroOutlookSummary;

